import { useState } from 'react'
import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Modal from '@cloudscape-design/components/modal'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Spinner from '@cloudscape-design/components/spinner'
import Table from '@cloudscape-design/components/table'
import { formatDate } from '@/utils/format'
import type { GbpReviewSummary } from '@/lib/types'
import AIAnnotation from './AIAnnotation'
import { InsightBullet } from '../../../lib/types'

interface Props {
  reviews: GbpReviewSummary | null
  loading: boolean
  clientId: string | null
  insights?: InsightBullet[]
}

export default function GbpReviewsWidget({ reviews, loading, clientId, insights }: Props) {
  const [selectedRating, setSelectedRating] = useState<number | null>(null)

  if (loading) {
    return (
      <Box textAlign="center" padding="xl">
        <Spinner size="large" nativeAttributes={{ 'aria-label': 'Loading' }} />
      </Box>
    )
  }
  if (!reviews) {
    return (
      <Alert
        statusIconAriaLabel="Info"
        header="Configure your business profile"
        action={
          clientId ? (
            <Button href={`/workspace/clients/${clientId}/configure`}>Configure now</Button>
          ) : undefined
        }
      >
        Set up your Google Business Profile to see reviews and ratings here.
      </Alert>
    )
  }

  const sorted = [...reviews.ratingDistribution].sort((a, b) => b.rating - a.rating)
  const withReviews = sorted.filter(r => r.count > 0)

  const filteredReviews =
    selectedRating !== null
      ? reviews.recentReviews.filter(r => r.rating === selectedRating)
      : []

  return (
    <div>
      <SpaceBetween size="s">
        <Box variant="h3" textAlign="center">
          {reviews.averageRating} ★ average · {reviews.totalReviews} reviews
        </Box>
        {/* Mobile Cards view not needed: this table has only 2 narrow columns (Rating, Count)
            that fit on any screen without horizontal scrolling. The row-click interaction
            (opens a review detail modal) also maps poorly to a Cards layout. */}
        <Table
          columnDefinitions={[
            { id: 'rating', header: 'Rating', cell: (item) => `${item.rating}★` },
            { id: 'count', header: 'Count', cell: (item) => item.count },
          ]}
          items={withReviews}
          variant="embedded"
          stripedRows
          onRowClick={(e) => setSelectedRating(e.detail.item.rating)}
        />
        <AIAnnotation bullets={insights ?? []} />
      </SpaceBetween>

      <Modal
        visible={selectedRating !== null}
        onDismiss={() => setSelectedRating(null)}
        header={selectedRating !== null ? `${selectedRating}★ Reviews` : ''}
        footer={
          <Box float="right">
            <Button variant="link" onClick={() => setSelectedRating(null)}>
              Close
            </Button>
          </Box>
        }
      >
        {filteredReviews.length === 0 ? (
          <Box variant="p" color="text-body-secondary">
            No recent reviews available for this rating.
          </Box>
        ) : (
          <SpaceBetween size="m">
            {filteredReviews.map((review) => (
              <SpaceBetween key={`${review.authorName}-${review.createdAt}-${review.rating}`} size="xxs">
                <Box variant="strong">{review.authorName}</Box>
                <Box variant="small" color="text-body-secondary">
                  {formatDate(review.createdAt)} · {review.replyStatus}
                </Box>
                {review.text !== null ? (
                  <Box variant="p">{review.text}</Box>
                ) : (
                  <Box variant="p" color="text-body-secondary">
                    <Box variant="small">No written review</Box>
                  </Box>
                )}
              </SpaceBetween>
            ))}
          </SpaceBetween>
        )}
      </Modal>
    </div>
  )
}
